import firebaseService from '../firebase/firebaseService';
import axios, { AxiosProgressEvent } from 'axios';

export const baseUrl =
  !process.env.NODE_ENV || process.env.NODE_ENV === 'development'
    ? 'https://api-dev.klydoclock.com'
    : 'https://api.klydoclock.com';
// Unslash this for local debugging
// 'http://localhost:10010';

export const getToken = async () => {
  return firebaseService.getToken();
};

export const getUserId = async () => {
  return firebaseService.getUserId();
};

const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
let sessionId = crypto.randomUUID();
let lastActivity = Date.now();

export function getSessionId(): string {
  const now = Date.now();
  if (now - lastActivity > SESSION_TIMEOUT_MS) {
    sessionId = crypto.randomUUID();
  }
  lastActivity = now;
  return sessionId;
}

const ANON_ID_KEY = 'klydo_anonymous_id';

export function getOrCreateAnonymousId(): string {
  let id = localStorage.getItem(ANON_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(ANON_ID_KEY, id);
  }
  return id;
}

async function getHeaders() {
  return {
    Authorization: `Bearer ${await getToken()}`,
    'client-session-id': getSessionId(),
    'client-anonymous-id': getOrCreateAnonymousId(),
  };
}

export type RequestInput = {
  url: string;
  params?: object;
  body?: object;
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
  signal?: AbortSignal;
};

export const baseClient = {
  get: async <T>({
    url,
    params,
    signal,
  }: Pick<RequestInput, 'url' | 'params' | 'signal'>): Promise<T> => {
    return await axios
      .get(baseUrl + url, {
        headers: await getHeaders(),
        params,
        signal,
      })
      .then((res) => {
        return res.data;
      });
  },
  put: async <T>({ url, params, body, signal }: RequestInput): Promise<T> => {
    return await axios
      .put(baseUrl + url, body, {
        headers: await getHeaders(),
        params,
        signal,
      })
      .then((res) => res.data);
  },
  post: async <T>({
    url,
    params,
    body,
    onUploadProgress,
    signal,
  }: RequestInput): Promise<T> => {
    return await axios
      .post(baseUrl + url, body, {
        headers: await getHeaders(),
        params,
        onUploadProgress,
        signal,
      })
      .then((res) => res.data);
  },
  delete: async <T>({ url, params, signal }: RequestInput): Promise<T> => {
    return await axios
      .delete(baseUrl + url, {
        headers: await getHeaders(),
        params,
        signal,
      })
      .then((res) => res.data);
  },
};
